// ────────────────────────────────────────────────────────────────────────
// Theme-Controller — zwei fixe Modi (Dark/Light).
//
// - Default = Systemeinstellung (prefers-color-scheme). Nach manuellem
//   Umschalten wird die explizite Wahl in localStorage gemerkt.
// - Setzt data-theme (eigene Tokens) UND data-bs-theme (Bootstrap 5.3) auf
//   <html>, aktualisiert die PWA-theme-color-Meta und die native Status-Bar.
// - Reine Logik ist vanilla (geteilt mit dem No-Flash-Boot-Script in
//   public/index.html); useThemeMode() ist die dünne React-Anbindung.
// ────────────────────────────────────────────────────────────────────────
import { useEffect, useState, useCallback } from 'react';
import { applyStatusBarForMode } from './nativeBridge';

export const STORAGE_KEY = 'mkw_theme_mode';

// Topbar-Farbe je Modus — für die PWA-/Browser-Chrome-Statusleiste.
const META_THEME_COLOR = { dark: '#0f1923', light: '#ffffff' };

export function getStoredMode() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function getSystemMode() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

export function resolveMode() {
  return getStoredMode() ?? getSystemMode();
}

export function applyMode(mode) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  root.setAttribute('data-bs-theme', mode);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_THEME_COLOR[mode] || META_THEME_COLOR.dark);

  // Native Status-Bar (No-Op im Browser)
  applyStatusBarForMode(mode);
}

export function setMode(mode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* Private-Mode o. Ä. — Wahl gilt dann nur für diese Session */
  }
  applyMode(mode);
}

export function toggleMode() {
  const next = resolveMode() === 'dark' ? 'light' : 'dark';
  setMode(next);
  return next;
}

// Folgt der Systemeinstellung — aber NUR solange keine explizite Wahl
// gespeichert ist. Gibt eine Cleanup-Funktion zurück.
export function initThemeWatcher() {
  let mql;
  try {
    mql = window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return () => {};
  }
  const handler = (e) => {
    if (getStoredMode() === null) applyMode(e.matches ? 'dark' : 'light');
  };
  if (mql.addEventListener) mql.addEventListener('change', handler);
  else if (mql.addListener) mql.addListener(handler); // ältere Browser
  return () => {
    if (mql.removeEventListener) mql.removeEventListener('change', handler);
    else if (mql.removeListener) mql.removeListener(handler);
  };
}

// React-Anbindung: liefert { mode, toggle } und hält data-theme synchron.
export function useThemeMode() {
  const [mode, setModeState] = useState(resolveMode);

  useEffect(() => {
    applyMode(mode);
    const cleanup = initThemeWatcher();
    // System-Änderung ggf. in den State spiegeln, wenn keine explizite Wahl.
    const sync = () => {
      if (getStoredMode() === null) setModeState(getSystemMode());
    };
    let mql;
    try {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
      if (mql.addEventListener) mql.addEventListener('change', sync);
      else if (mql.addListener) mql.addListener(sync);
    } catch {
      /* ignore */
    }
    return () => {
      cleanup();
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', sync);
        else if (mql.removeListener) mql.removeListener(sync);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(() => {
    setModeState(toggleMode());
  }, []);

  return { mode, toggle };
}
